import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getFcaQuestionnaire, postFcaQuestionnaire } from '@/services/api/profile';
import { AccountType, getAccountType } from '@/utils/account-type';
import { userAttributesQueryKey } from './use-user-attributes';

/** The FCA self-certification is only asked of UK residents. */
export const FCA_QUESTIONNAIRE_COUNTRY = 'GB';

const profileFcaQuestionnaireQueryKey = (pid: UserPid) => ['profile', 'fca-questionnaire', pid];

const profileFcaQuestionnaireQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: getFcaQuestionnaire,
    queryKey: profileFcaQuestionnaireQueryKey(pid),
  });

const useProfileFcaQuestionnaire = (enabled = true) => {
  const { pid } = useUserPid();
  const { enabled: queryOptionsEnabled, ...queryOptions } = profileFcaQuestionnaireQueryOptions(pid);

  return useQuery({
    ...queryOptions,
    enabled: queryOptionsEnabled && enabled,
  });
};

const useProfileFcaQuestionnaireEligibility = (countryOfResidency?: string, accountType = getAccountType()) => {
  const isRequired = countryOfResidency === FCA_QUESTIONNAIRE_COUNTRY;
  const { data, ...queryResult } = useProfileFcaQuestionnaire(isRequired);
  const eligibility = accountType === AccountType.FUTURES || (data ? data.answers.selfCertification !== 'none' : null);

  return {
    ...queryResult,
    data: eligibility,
    isComplete: !isRequired || eligibility !== null,
    isRequired,
  };
};

const useProfileFcaQuestionnaireMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postFcaQuestionnaire,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: profileFcaQuestionnaireQueryKey(pid) });
      queryClient.invalidateQueries({ queryKey: userAttributesQueryKey(pid) });
    },
  });
};

export { useProfileFcaQuestionnaire, useProfileFcaQuestionnaireEligibility, useProfileFcaQuestionnaireMutation };
